import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";

// Cache in-memory delle ui_prefs server (load una sola volta per sessione)
let _cache = null;
let _inflight = null;

async function fetchUiPrefs() {
  if (_cache) return _cache;
  if (!_inflight) {
    _inflight = api.get("/me/ui-prefs").then((r) => r.data).catch(() => ({}));
  }
  const data = await _inflight;
  _inflight = null;
  _cache = data || {};
  return _cache;
}

/**
 * Hook per leggere e salvare una preferenza UI sincronizzata cross-device.
 * Flusso: localStorage (sync, fast) → server (cross-device merge).
 *
 * @param {string} key - identificatore chiave (es. "clients_sort_by")
 * @param {*} defaultValue - valore di default se non trovato
 * @returns {[value, setValue]}
 */
export function useSyncedPref(key, defaultValue) {
  const [value, setValue] = useState(() => {
    try {
      const raw = localStorage.getItem(key);
      if (raw != null) {
        try { return JSON.parse(raw); } catch { return raw; }
      }
    } catch { /* noop */ }
    return defaultValue;
  });

  // Carica dal server al mount → se differente, aggiorna
  useEffect(() => {
    let cancelled = false;
    fetchUiPrefs().then((prefs) => {
      const serverVal = prefs?.[key];
      if (!cancelled && serverVal !== undefined && serverVal !== null && JSON.stringify(serverVal) !== JSON.stringify(value)) {
        setValue(serverVal);
        try { localStorage.setItem(key, typeof serverVal === "string" ? serverVal : JSON.stringify(serverVal)); } catch { /* noop */ }
      }
    });
    return () => { cancelled = true; };
  }, [key]);

  // Debounce salvataggio server: 800ms dall'ultima modifica
  const timer = useRef(null);
  useEffect(() => {
    try { localStorage.setItem(key, typeof value === "string" ? value : JSON.stringify(value)); } catch { /* noop */ }
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      // Aggiorna cache locale + server
      if (_cache) _cache = { ..._cache, [key]: value };
      api.put("/me/ui-prefs", { [key]: value }).catch(() => { /* silenzioso */ });
    }, 800);
    return () => { if (timer.current) clearTimeout(timer.current); };
  }, [key, value]);

  return [value, setValue];
}
